import React, { useState } from "react";
import { Info, ArrowUpDown } from "lucide-react";

export const paise = (p, { signed = false } = {}) => {
  if (p == null) return "—";
  const v = Math.abs(p) / 100;
  const s = `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  if (p < 0) return `−${s}`;
  return signed && p > 0 ? `+${s}` : s;
};
export const num = (n) => (n || 0).toLocaleString("en-IN");

export function SectionTitle({ children, testid, note }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mb-3">
      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400" data-testid={testid}>{children}</h2>
      {note && <span className="text-[11px] text-slate-400">{note}</span>}
    </div>
  );
}

export function MetricCard({ label, value, hint, tip, accent = "white", negative = false, testid }) {
  const accents = {
    navy: "bg-[#0A2540] text-white",
    gold: "bg-[#FBF4DF] text-[#0A2540] border border-[#F1E3B5]",
    emerald: "bg-emerald-50 text-emerald-900 border border-emerald-200",
    rose: "bg-rose-50 text-rose-900 border border-rose-200",
    white: "bg-white text-[#0A2540] border border-slate-200",
  }[accent];
  return (
    <div className={`rounded-2xl p-5 ${accents}`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        <p className="text-[11px] uppercase tracking-widest opacity-70 font-bold">{label}</p>
        {tip && (
          <span title={tip} className="opacity-60 hover:opacity-100 cursor-help" data-testid={`${testid}-tip`} aria-label={tip}>
            <Info size={12} />
          </span>
        )}
      </div>
      <p className={`mt-2 text-2xl font-extrabold ${negative ? "text-rose-600" : ""}`} style={{ fontFamily: "Poppins" }} data-testid={`${testid}-value`}>{value}</p>
      {hint && <p className="mt-1 text-xs opacity-80">{hint}</p>}
    </div>
  );
}

const ECON_COLS = [
  { key: "consultations", label: "Consultations", fmt: num },
  { key: "customer_gmv_paise", label: "Customer GMV", fmt: paise, tip: "Total charged to customers, incl. GST" },
  { key: "lawyer_fees_paise", label: "Lawyer fees", fmt: paise, tip: "Protected lawyer professional fee (snapshot)" },
  { key: "gross_margin_paise", label: "Advoset gross margin", fmt: paise, tip: "Customer price before GST − lawyer fee" },
  { key: "other_expenses_paise", label: "Other expenses", fmt: paise, tip: "Configured estimate from the pricing config version frozen at booking" },
  { key: "est_contribution_paise", label: "Est. contribution", fmt: paise, tip: "Gross margin − other expenses (GST excluded)" },
  { key: "avg_contribution_paise", label: "Avg contribution", fmt: paise, tip: "Est. contribution ÷ consultations" },
];

export function EconTable({ rows, firstCol, sortable = false, testid, emptyText, extraCols = [] }) {
  const [sort, setSort] = useState({ key: null, dir: -1 });
  if (!rows || rows.length === 0 || rows.every((r) => !r.consultations)) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-400" data-testid={`${testid}-empty`}>
        {emptyText || "No snapshot-based pricing data yet. New level-priced consultations will appear here."}
      </div>
    );
  }
  const cols = [...ECON_COLS, ...extraCols];
  const sorted = sort.key ? [...rows].sort((a, b) => ((a[sort.key] ?? -Infinity) - (b[sort.key] ?? -Infinity)) * sort.dir) : rows;
  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }));
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
            <th className="px-3 py-2.5 font-bold">{firstCol.label}</th>
            {cols.map((c) => (
              <th key={c.key} className="px-3 py-2.5 font-bold text-right whitespace-nowrap" title={c.tip}>
                {sortable ? (
                  <button type="button" onClick={() => toggle(c.key)} className="inline-flex items-center gap-1 hover:text-[#0A2540]" data-testid={`${testid}-sort-${c.key}`}>
                    {c.label} <ArrowUpDown size={11} className={sort.key === c.key ? "text-[#0A2540]" : "opacity-40"} />
                  </button>
                ) : c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r[firstCol.key]} className="border-b border-slate-50 last:border-0" data-testid={`${testid}-row-${r[firstCol.key]}`}>
              <td className="px-3 py-2 font-bold text-[#0A2540] whitespace-nowrap">{firstCol.render ? firstCol.render(r) : r[firstCol.key]}</td>
              {cols.map((c) => {
                const v = r[c.key];
                const neg = typeof v === "number" && v < 0 && c.key.includes("contribution");
                return (
                  <td key={c.key} className={`px-3 py-2 text-right tabular-nums ${neg ? "text-rose-600 font-bold" : ""} ${!r.consultations ? "text-slate-300" : ""}`}
                      data-testid={`${testid}-${r[firstCol.key]}-${c.key}`}>
                    {c.fmt(v)}{c.key === "other_expenses_paise" && r.expense_unknown_count > 0 ? "*" : ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
